import { useState } from 'react';
import {
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import Add from '@mui/icons-material/Add';
import { Link } from 'react-router-dom';
import { useCurrentUser } from '~/shared/hooks/useCurrentUser';
import { isValid } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { toHtmlDate } from '~/shared/utils/toHtmlDate';
import type { UpdateMatterInput } from '~/gql/graphql';
import type { MatterViewModel } from '~/modules/matters/matters.types';
import {
  travelPlansLabel,
  writtenReportNeededLabel,
} from '~/modules/matters/matters.types';

interface Props {
  matter: MatterViewModel;
  matterId: string | undefined;
  isInvitedOnlyExpert: boolean;
  isAttorney: boolean;
  expertStripeConnected: boolean;
  nextDeadlineEdit: string;
  setNextDeadlineEdit: (value: string) => void;
  showDeadlinePicker: boolean;
  setShowDeadlinePicker: (value: boolean) => void;
  updateMatter: (
    matterId: string,
    patch: Partial<UpdateMatterInput>,
  ) => Promise<void>;
}

export const MatterDetailsPanel = ({
  matter,
  matterId,
  isInvitedOnlyExpert,
  isAttorney,
  expertStripeConnected,
  nextDeadlineEdit,
  setNextDeadlineEdit,
  showDeadlinePicker,
  setShowDeadlinePicker,
  updateMatter,
}: Props) => {
  const { user: currentUserForStripe } = useCurrentUser();
  const hasStripeConnectAccount = Boolean(
    currentUserForStripe?.stripeConnectAccountId,
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [deadlineError, setDeadlineError] = useState<string | undefined>();

  const invitedExpertPayoutNotice = hasStripeConnectAccount
    ? "You've been invited to this matter. Finish payout setup in Stripe to request a retainer; once the attorney accepts, you'll have access to billing."
    : "You've been invited to this matter. Set up your Stripe payout account to request a retainer; once the attorney accepts, you'll have access to billing.";
  const invitedExpertPayoutLinkLabel = hasStripeConnectAccount
    ? 'Continue payout setup'
    : 'Set up payout account';

  const nextDeadlineDate = matter.nextDeadline
    ? new Date(matter.nextDeadline)
    : null;
  const isValidDeadline =
    nextDeadlineDate !== null && isValid(nextDeadlineDate);
  const nextDeadlineLabel =
    nextDeadlineDate && isValidDeadline
      ? formatInTimeZone(nextDeadlineDate, 'UTC', 'MMM d, yyyy')
      : null;

  const handleSaveDeadline = async () => {
    if (!matterId) {
      return;
    }
    setIsUpdating(true);
    setDeadlineError(undefined);
    try {
      await updateMatter(matterId, {
        nextDeadline: nextDeadlineEdit.trim() === '' ? null : nextDeadlineEdit,
      });
      setShowDeadlinePicker(false);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'matter_save_deadline' },
        extra: { matterId: matterId ?? null },
      });
      setDeadlineError(
        error instanceof Error
          ? error.message
          : 'Failed to save deadline. Please try again.',
      );
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      {isInvitedOnlyExpert && !expertStripeConnected && (
        <InvitedExpertEmptyStack>
          <InviteNoticeText variant="body2" color="text.secondary">
            {invitedExpertPayoutNotice}
          </InviteNoticeText>
          <Button
            component={Link}
            to="/connect/setup-payouts"
            variant="outlined"
            size="medium"
            sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
          >
            {invitedExpertPayoutLinkLabel}
          </Button>
        </InvitedExpertEmptyStack>
      )}
      {isInvitedOnlyExpert && expertStripeConnected && (
        <InviteNoticeText variant="body2" color="text.secondary">
          You&apos;ve been invited to this matter. Request a retainer; once the
          attorney accepts, you&apos;ll have access to billing.
        </InviteNoticeText>
      )}
      {!isInvitedOnlyExpert && (
        <MatterDetailsStack>
          <DescriptionBlock>
            <FieldLabel variant="caption" color="text.secondary">
              Brief case description
            </FieldLabel>
            <DescriptionText variant="body2">
              {matter.caseDescription.trim() || '—'}
            </DescriptionText>
          </DescriptionBlock>
          <CaseInfoRowStack>
            <MetaItemBlock>
              <FieldLabel variant="caption" color="text.secondary">
                Case number
              </FieldLabel>
              <Typography variant="body2">
                {matter.caseNumber?.trim() || '—'}
              </Typography>
            </MetaItemBlock>
            <MetaItemBlock>
              <FieldLabel variant="caption" color="text.secondary">
                Court
              </FieldLabel>
              <Typography variant="body2">
                {matter.courtName?.trim() || '—'}
              </Typography>
            </MetaItemBlock>
            <MetaItemBlock>
              <FieldLabel variant="caption" color="text.secondary">
                Location
              </FieldLabel>
              <Typography variant="body2">
                {matter.location?.trim() || '—'}
              </Typography>
            </MetaItemBlock>
          </CaseInfoRowStack>
          <MetaRowStack>
            <DeadlineCell>
              <FieldLabel variant="caption" color="text.secondary">
                Next deadline
              </FieldLabel>
              {isAttorney && showDeadlinePicker ? (
                <DeadlineActionsStack>
                  <TextField
                    size="small"
                    type="date"
                    value={nextDeadlineEdit}
                    onChange={(e) => setNextDeadlineEdit(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 160 }}
                    inputProps={{ 'aria-label': 'Next deadline' }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={isUpdating}
                    onClick={handleSaveDeadline}
                    startIcon={
                      isUpdating ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : undefined
                    }
                  >
                    {isUpdating ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    disabled={isUpdating}
                    onClick={() => {
                      setDeadlineError(undefined);
                      setNextDeadlineEdit(toHtmlDate(matter.nextDeadline));
                      setShowDeadlinePicker(false);
                    }}
                  >
                    Cancel
                  </Button>
                  {deadlineError && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ width: '100%' }}
                    >
                      {deadlineError}
                    </Typography>
                  )}
                </DeadlineActionsStack>
              ) : (
                <DeadlineActionsStack>
                  {matter.nextDeadline && (
                    <Typography variant="body2">
                      {nextDeadlineLabel ?? '—'}
                    </Typography>
                  )}
                  {!matter.nextDeadline && !isAttorney && (
                    <Typography variant="body2">—</Typography>
                  )}
                  {isAttorney && (
                    <DeadlineEditButton
                      size="small"
                      variant="text"
                      startIcon={
                        !matter.nextDeadline ? (
                          <Add sx={{ fontSize: 18 }} />
                        ) : undefined
                      }
                      onClick={() => {
                        setNextDeadlineEdit(toHtmlDate(matter.nextDeadline));
                        setShowDeadlinePicker(true);
                      }}
                    >
                      {matter.nextDeadline ? 'Edit' : 'Add Deadline'}
                    </DeadlineEditButton>
                  )}
                </DeadlineActionsStack>
              )}
            </DeadlineCell>
            <KeyDatesCell>
              <FieldLabel variant="caption" color="text.secondary">
                Key dates
              </FieldLabel>
              <KeyDatesText variant="body2">
                {matter.keyDates || '—'}
              </KeyDatesText>
            </KeyDatesCell>
          </MetaRowStack>
          <MetaDetailsRowStack>
            <MetaItemBlock>
              <FieldLabel variant="caption" color="text.secondary">
                Travel Plans
              </FieldLabel>
              <Typography variant="body2">
                {matter.travelPlans
                  ? travelPlansLabel[matter.travelPlans]
                  : '—'}
              </Typography>
            </MetaItemBlock>
            <MetaItemBlock>
              <FieldLabel variant="caption" color="text.secondary">
                Written report
              </FieldLabel>
              <Typography variant="body2">
                {matter.writtenReportNeeded
                  ? writtenReportNeededLabel[matter.writtenReportNeeded]
                  : '—'}
              </Typography>
            </MetaItemBlock>
          </MetaDetailsRowStack>
        </MatterDetailsStack>
      )}
    </>
  );
};

const InviteNoticeText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 700,
}));

const InvitedExpertEmptyStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  display: 'block',
  marginBottom: theme.spacing(0.75),
}));

const DescriptionText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body1.fontSize,
}));

const DeadlineEditButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 0,
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(0.5),
  color: theme.palette.amount.main,
  '&:hover': {
    color: theme.palette.amount.dark,
    backgroundColor: theme.palette.action.hover,
  },
  '& .MuiButton-startIcon': { marginRight: 0 },
}));

const KeyDatesText = styled(Typography)(() => ({
  wordBreak: 'break-word',
}));

const MatterDetailsStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}));

const DescriptionBlock = styled(Stack)(() => ({
  minWidth: 0,
}));

const MetaRowStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  flexWrap: 'wrap',
  gap: theme.spacing(2.5),
  minWidth: 0,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: theme.spacing(4),
  },
}));

const DeadlineCell = styled(Stack)(({ theme }) => ({
  minWidth: 0,
  [theme.breakpoints.up('md')]: {
    flex: '0 0 auto',
  },
}));

const DeadlineActionsStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

const KeyDatesCell = styled(Stack)(({ theme }) => ({
  minWidth: 0,
  [theme.breakpoints.up('md')]: {
    flex: '1 1 0',
  },
}));

const CaseInfoRowStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  flexShrink: 0,
  minWidth: 0,
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: theme.spacing(4),
  },
}));

const MetaDetailsRowStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  flexShrink: 0,
  minWidth: 0,
  paddingTop: theme.spacing(2),
  borderTop: '1px solid',
  borderColor: theme.palette.divider,
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const MetaItemBlock = styled(Stack)(() => ({
  minWidth: 0,
}));
